import { useState } from 'preact/hooks';
import { useMe } from '../auth/useMe';

/**
 * Heart-button hook backed by /api/me/likes (ADR-0015 Phase 1.1). The
 * initial value comes from `pkg.likedByMe` on the catalog payload
 * (server-projected via the WorkLikesEmitHook, no client fan-out
 * required). Toggles persist via POST / DELETE on the same
 * /api/me/likes/{workId} path; optimistic local update keeps the
 * heart responsive while the network call is in flight.
 *
 * Returns `signedIn: false` for anonymous / unknown auth states; the
 * heart pill renders disabled in that case (the SPA hides the affordance
 * upstream too).
 */
export function useWorkLike(
    workId: string,
    initial: boolean,
): {
    liked: boolean;
    setLiked: (next: boolean) => Promise<void>;
    toggle: () => Promise<void>;
    signedIn: boolean;
} {
    const me = useMe();
    const [liked, setLikedState] = useState<boolean>(initial);

    const signedIn = me.status === 'authenticated';

    const setLiked = async (next: boolean) => {
        if (!signedIn) return;
        if (!workId) return;
        const previous = liked;
        // Optimistic update - flip first, revert on network failure.
        setLikedState(next);
        try {
            const url = `/api/me/likes/${encodeURIComponent(workId)}`;
            const init: RequestInit = {
                method: next ? 'POST' : 'DELETE',
                credentials: 'include',
                headers: next ? { 'Content-Type': 'application/json' } : undefined,
                body: next ? '{}' : undefined,
            };
            const res = await fetch(url, init);
            if (!res.ok && res.status !== 204) {
                throw new Error(`HTTP ${res.status}`);
            }
        } catch {
            setLikedState(previous);
        }
    };

    const toggle = () => setLiked(!liked);

    return { liked, setLiked, toggle, signedIn };
}
